/**
 * Render a text array editor with checkboxes for selecting values from a predefined list.
 * This editor maintains the selection order based on user interaction, not checkbox display order.
 * 
 * @param {Object} container - D3 selection for the container element
 * @param {Object} fieldDef - Field definition with values array
 * @param {string} fieldId - Unique ID for form field identification
 * @param {Array<string>} currentValue - Current selected values
 * @returns {Array<string>} Reference to the selectionOrder array
 */
export function renderTextArrayEditor(container, fieldDef, fieldId, currentValue) {
  // Text array editor: checkboxes for each allowed value
  const textarrayContainer = container.append('div').attr('class', 'textarray-editor');
  const existingValues = currentValue ? currentValue : [];
  
  // Track selection order with array (maintains order) - initialized from existing data
  const selectionOrder = [...existingValues];
  
  // Always use stable order from fieldDef.values config
  fieldDef.values.forEach(value => {
    const valueRow = textarrayContainer.append('div')
      .attr('class', 'textarray-row')
      .style('margin-bottom', '6px');
    
    const checkbox = valueRow.append('input')
      .attr('type', 'checkbox')
      .attr('id', `${fieldId}-${value}`)
      .attr('class', 'textarray-checkbox')
      .attr('value', value)
      .property('checked', existingValues.includes(value));
    
    valueRow.append('label')
      .attr('for', `${fieldId}-${value}`)
      .style('margin-left', '8px')
      .text(value);
  });
  
  // Add preview field
  const previewDiv = container.append('div')
    .attr('class', 'textarray-preview')
    .style('margin-top', '10px')
    .style('padding', '8px')
    .style('border-radius', '3px')
    .style('font-family', 'monospace')
    .style('font-size', '0.9em')
    .style('white-space', 'pre-wrap')
    .style('word-break', 'break-all');
  
  // Function to update preview
  const updatePreview = () => {
    previewDiv.text(selectionOrder.join(", "));
  };
  
  // Initial preview
  updatePreview();
  
  // Update selection order on checkbox change
  textarrayContainer.selectAll('.textarray-checkbox').on('change', function() {
    const value = this.value;
    if (this.checked) {
      // Add to end if not already present
      if (!selectionOrder.includes(value)) {
        selectionOrder.push(value);
      }
    } else {
      // Remove from array
      const index = selectionOrder.indexOf(value);
      if (index > -1) {
        selectionOrder.splice(index, 1);
      }
    }
    updatePreview();
  });
  
  // Hidden input to mark this as textarray field and store selection order
  container.append('input')
    .attr('type', 'hidden')
    .attr('id', fieldId)
    .attr('data-textarray', 'true')
    .property('_selectionOrder', selectionOrder);
  
  return selectionOrder;
}
